import React, { useState, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { App, Tag, Button, Typography, Badge } from 'antd'
import { Link } from 'react-router-dom'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import format from 'date-fns/format'

import getDateInUserTimezone from '../../functions/get-date-in-user-timezone'
import { convertHourFormats } from '@vacationtracker/shared/functions/convert-between-hour-formats'
import { getDateFnsLocale } from '../../i18n'

import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { DisplayLeaveDuration } from '../display-leave-in-days-and-hours'
import { UserAvatar } from '@vacationtracker/shared/components/user-avatar'

import { LocaleEnum } from '../../types/i18n'
import { HourFormatEnum } from '../../types/user'
import { IApprover, ILeaveDayDetails, IPartDay } from '../../types/leave-request'
import { formatTimeString } from '../../functions/get-days-or-hours'
import { ApproversLevelEnum } from '../../types/team'

interface ILeavesBox {
  leaveRequestId: string
  userId: string
  userImage: string
  userName: string
  leaveTypeName: string
  leaveTypeColor: string
  isHiddenLeaveType: boolean
  leaveStatus: string
  type: string
  approver?: IApprover
  approversName?: string[]
  autoApproved?: boolean
  startDate: string
  endDate: string
  role: string
  workingDays: number
  isPartDay: boolean
  daysList: ILeaveDayDetails[]
  onUpdateLeaveRequest?: (e) => void
  onCancelLeave?: (e) => void
  handleDenyWithReason: (e) => void
  // When provided, the Edit button opens a callback-owned modal instead of
  // navigating to /app/leaves/{id}/edit. Same pattern as Wallchart.
  onEditLeave?: (leaveRequestId: string) => void
  reason?: string
  partDay?: IPartDay
  partDayStartHour?: number
  partDayEndHour?: number
  editLeaves?: boolean
  approverToUsers: {
    id: string
  }[]
  isEdited?: boolean
  hourlyLeaveAccounting: boolean
  locale: LocaleEnum
  hourFormat?: HourFormatEnum
  isToil?: boolean
  approvedBySubstituteApprover?: boolean
  substituteApproverSet?: boolean
  approvedLevel?: ApproversLevelEnum
  isMultilevelApproval?: boolean
  hasWorkingHoursSchedule?: boolean
  isMicrosoftTabs?: boolean
  isLocationAdminForLeave?: boolean
  greyOutWhenNotLinkable?: boolean
}

const { Text } = Typography

const LeavesBox = ({
  leaveRequestId,
  userId,
  userImage,
  userName,
  leaveTypeName,
  leaveTypeColor,
  isHiddenLeaveType,
  leaveStatus,
  type,
  approver,
  approversName,
  autoApproved = false,
  startDate,
  endDate,
  role,
  workingDays,
  hasWorkingHoursSchedule,
  daysList,
  hourlyLeaveAccounting,
  isPartDay,
  onUpdateLeaveRequest,
  handleDenyWithReason,
  onCancelLeave,
  onEditLeave,
  reason,
  partDayStartHour,
  partDayEndHour,
  partDay,
  approverToUsers,
  isEdited,
  locale,
  hourFormat,
  isToil = false,
  approvedBySubstituteApprover = false,
  substituteApproverSet = false,
  approvedLevel,
  isMultilevelApproval = false,
  isMicrosoftTabs = false,
  editLeaves,
  isLocationAdminForLeave = false,
  greyOutWhenNotLinkable = true,
}: ILeavesBox): React.ReactElement => {
  const { formatMessage } = useIntl()
  const { modal } = App.useApp()
  const [approveBtnLoading, setApproveBtnLoading] = useState(false)
  // Tracks the app-level `body.dark-theme` class so LeavesBox can swap its border
  // and text colors without every consumer having to thread an `isDarkTheme` prop.
  const [isDarkTheme, setIsDarkTheme] = useState<boolean>(() => typeof document !== 'undefined' && document.body.classList.contains('dark-theme'))
  useEffect(() => {
    if (typeof document === 'undefined') return
    const sync = () => setIsDarkTheme(document.body.classList.contains('dark-theme'))
    const observer = new MutationObserver(sync)
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])
  const cardBorderColor = isDarkTheme ? 'rgb(73, 88, 98)' : '#f0f0f0'
  const userNameColor = isDarkTheme ? 'rgb(209, 163, 255)' : '#7f00ff'
  // Union (not role precedence): Admin OR location-admin of this leave's location OR approver of this user.
  // A LocationAdmin+Approver combo must keep approver access even when the user is outside managed locations.
  const isAdminOrUsersApprover = role === 'Admin' || isLocationAdminForLeave || Boolean(approverToUsers?.find((user) => user.id === userId))
  const hideThisLeaveType = isHiddenLeaveType && !isAdminOrUsersApprover
  // Non-linkable avatars stay greyed unless the caller opts out (e.g. home dashboard wants them plain).
  const avatarIsLinkToPage = isMicrosoftTabs || (!isAdminOrUsersApprover && !greyOutWhenNotLinkable) ? undefined : Boolean(isAdminOrUsersApprover)
  let convertedStartHour
  let convertedEndHour
  if (isPartDay && (partDay || (typeof partDayStartHour === 'number' && typeof partDayEndHour === 'number'))) {
    const {
      value: startHourValue,
      minute: startMinute,
      amOrPm: amOrPmStart,
    } = convertHourFormats(hourFormat as HourFormatEnum, partDay ? partDay.startHour : (partDayStartHour as number), partDay ? (partDay.startMinute as number) : 0)
    const {
      value: endHourValue,
      minute: endMinute,
      amOrPm: amOrPmEnd,
    } = convertHourFormats(hourFormat as HourFormatEnum, partDay ? partDay.endHour : (partDayEndHour as number), partDay ? (partDay.endMinute as number) : 0)
    // If either side has non-zero minutes, force :00 padding on the whole-hour side so
    // the two times stay visually consistent (e.g. "10:00 – 10:30" instead of "10 – 10:30").
    const showZeroPadding = startMinute > 0 || endMinute > 0
    convertedStartHour = formatTimeString({ hour: startHourValue, minute: startMinute, showZeroPadding, hourFormat, amOrPm: amOrPmStart })
    convertedEndHour = formatTimeString({ hour: endHourValue, minute: endMinute, showZeroPadding, hourFormat, amOrPm: amOrPmEnd })
  }

  const showCancelConfirm = () => {
    void modal.confirm({
      title: formatMessage({ id: 'app.cancelLeave' }),
      icon: <ExclamationCircleOutlined />,
      content: formatMessage({ id: 'components.leaveBox.cancelLeaveConfirm' }),
      okText: formatMessage({ id: 'app.yes' }),
      okType: 'danger',
      cancelText: formatMessage({ id: 'app.no' }),
      onOk() {
        if (onCancelLeave) {
          onCancelLeave({
            id: leaveRequestId,
            user: {
              id: userId,
            },
          })
        }
      },
    })
  }

  const handleApprove = () => {
    setApproveBtnLoading(true)
    if (onUpdateLeaveRequest) {
      onUpdateLeaveRequest(true)
    }
  }

  const leaveMessage = () => {
    const leaveStartDateInTimezone = getDateInUserTimezone(startDate)
    const leaveEndDateInTimezone = getDateInUserTimezone(endDate)
    const dateFnsLocale = getDateFnsLocale(locale)
    const leaveStartDate = format(new Date(leaveStartDateInTimezone), 'MMMM do yyyy', { locale: dateFnsLocale })
    const leaveEndDate = format(new Date(leaveEndDateInTimezone), 'MMMM do yyyy', { locale: dateFnsLocale })
    // Short form used for the compact description: "Aug 31 – Sep 3".
    const leaveStartDateShort = format(new Date(leaveStartDateInTimezone), 'MMM d', { locale: dateFnsLocale })
    const leaveEndDateShort = format(new Date(leaveEndDateInTimezone), 'MMM d', { locale: dateFnsLocale })
    // Reused by all pending variants: renders "N days" or "N hours" per user's schedule/hourly settings.
    const durationNode = <DisplayLeaveDuration value={workingDays} daysList={daysList} hasSchedule={hasWorkingHoursSchedule} />
    return (
      <>
        {leaveStatus === 'APPROVED' && leaveStartDate === leaveEndDate && !isPartDay && (
          <IntlMessages
            id="components.leaveBox.leavesUpcomingOneDayInfo"
            values={{
              leaveStartDate: leaveStartDateShort,
              duration: durationNode,
            }}
          />
        )}
        {leaveStatus === 'APPROVED' && leaveStartDate !== leaveEndDate && !isPartDay && (
          <IntlMessages
            id="components.leaveBox.leavesOffTodayInfo"
            values={{
              leaveStartDate: leaveStartDateShort,
              leaveEndDate: leaveEndDateShort,
              duration: durationNode,
            }}
          />
        )}
        {leaveStatus === 'APPROVED' && isPartDay && (
          <IntlMessages
            id="components.leaveBox.leavesBoxHalfDay"
            values={{
              leaveStartDate: leaveStartDateShort,
              startHour: convertedStartHour,
              endHour: convertedEndHour,
              duration: durationNode,
            }}
          />
        )}
        {leaveStatus === 'OPEN' && leaveStartDate !== leaveEndDate && !isPartDay && (
          <>
            <IntlMessages
              id="components.leaveBox.leavesPendingInfo"
              values={{
                leaveStartDate: leaveStartDateShort,
                leaveEndDate: leaveEndDateShort,
                duration: durationNode,
              }}
            />
          </>
        )}
        {leaveStatus === 'OPEN' && leaveStartDate === leaveEndDate && !isPartDay && (
          <>
            <IntlMessages
              id="components.leaveBox.leavesPendingOneDay"
              values={{
                leaveStartDate: leaveStartDateShort,
                duration: durationNode,
              }}
            />
          </>
        )}
        {leaveStatus === 'OPEN' && isPartDay && (
          <>
            <IntlMessages
              id="components.leaveBox.leavesBoxHalfDayPending"
              values={{
                leaveStartDate: leaveStartDateShort,
                startHour: convertedStartHour,
                endHour: convertedEndHour,
                duration: durationNode,
              }}
            />
          </>
        )}
      </>
    )
  }

  const reviewedBy = () => {
    if (leaveStatus === 'APPROVED' && approvedBySubstituteApprover) {
      return (
        <Badge.Ribbon text={formatMessage({ id: 'app.substituteApprover' })} className="substitute-approver-ribbon">
          <IntlMessages id="components.leaveBox.approvedBy" />:{' '}
          <strong>
            {autoApproved ? (
              <IntlMessages id="app.autoApproved" />
            ) : (
              approversName?.map((name, index) => {
                return (
                  <span key={index}>
                    {index > 0 ? ', ' : ''}
                    {name}
                  </span>
                )
              })
            )}
          </strong>
        </Badge.Ribbon>
      )
    }
    if (leaveStatus === 'APPROVED') {
      return (
        <>
          <IntlMessages id="components.leaveBox.approvedBy" />:{' '}
          <strong>
            {autoApproved ? (
              <IntlMessages id="app.autoApproved" />
            ) : (
              approversName?.map((name, index) => {
                return (
                  <span key={index}>
                    {index > 0 ? ', ' : ''}
                    {name}
                  </span>
                )
              })
            )}
          </strong>
        </>
      )
    }
    return <></>
  }

  const isPending = leaveStatus === 'OPEN'
  const showActions = type !== 'OFFTODAY' && ((leaveStatus === 'APPROVED' && isAdminOrUsersApprover) || isPending)

  return (
    <div
      className="leaves-box"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: 12,
        border: `1px solid ${cardBorderColor}`,
        ...(isPending && { borderLeft: '3px solid #faad14' }),
        borderRadius: 8,
        marginBottom: 8,
        background: isDarkTheme ? 'transparent' : '#ffffff',
      }}
    >
      <UserAvatar
        id={userId}
        name={userName}
        shape="circle"
        avatarSize={40}
        avatar={userImage}
        isLinkToPage={avatarIsLinkToPage}
        greyOut={greyOutWhenNotLinkable && !isAdminOrUsersApprover}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Header: leave type tag, user name, status tag */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px', marginBottom: 4 }}>
          {!hideThisLeaveType && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <span
                aria-hidden="true"
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 4,
                  background: leaveTypeColor,
                  flexShrink: 0,
                }}
              />
              <Text style={{ fontSize: 14, fontWeight: 600 }}>{leaveTypeName}</Text>
            </span>
          )}
          {isToil && (
            <Tag color="green" style={{ margin: 0 }}>
              <IntlMessages id="app.toil" />
            </Tag>
          )}
          {!hideThisLeaveType && (
            <Text type="secondary" style={{ fontSize: 14 }}>
              ·
            </Text>
          )}
          <span style={{ fontSize: 14, fontWeight: 600, color: userNameColor }}>
            {isAdminOrUsersApprover && !isMicrosoftTabs ? (
              <Link to={`/app/users/${userId}`} style={{ color: 'inherit' }}>
                {userName}
              </Link>
            ) : (
              userName
            )}
          </span>
          {isPending && (
            <Tag style={{ color: '#faad14', background: '#fffbe6', borderColor: '#ffe58f', margin: 0 }}>
              <IntlMessages id="app.pending" />
            </Tag>
          )}
          {isEdited && (
            <Tag color="#7f00ff" style={{ margin: 0 }}>
              <IntlMessages id="app.edited" />
            </Tag>
          )}
        </div>

        {/* Description */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          {leaveMessage()}
        </Text>

        {/* Reason */}
        {reason && isAdminOrUsersApprover && (
          <div style={{ marginTop: 4, fontSize: 12 }}>
            <Text style={{ fontSize: 12 }}>
              <b>
                <IntlMessages id="app.reason" />:
              </b>{' '}
              {reason}
            </Text>
          </div>
        )}

        {/* Substitute approver warning */}
        {substituteApproverSet && (
          <div style={{ marginTop: 4 }}>
            <Text type="warning" style={{ fontSize: 12 }}>
              <IntlMessages id="app.substituteApproverSet" />
            </Text>
          </div>
        )}

        {/* Approved by (for approved leaves) */}
        {leaveStatus === 'APPROVED' && <div style={{ marginTop: 4, fontSize: 12 }}>{reviewedBy()}</div>}

        {/* Multilevel approval info */}
        {approvedLevel && (
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 12 }}>
              <IntlMessages id="app.approvedByFristLevel" values={{ approverName: approver?.name }} />
            </Text>
          </div>
        )}
        {isMultilevelApproval && !approvedLevel && (
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 12 }}>
              <IntlMessages id="bot.notification.infoFirstLevel" />
            </Text>
          </div>
        )}

        {/* Actions */}
        {showActions && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            {leaveStatus === 'APPROVED' && isAdminOrUsersApprover && (
              <Button size="small" onClick={showCancelConfirm} danger>
                <IntlMessages id="app.cancelLeave" />
              </Button>
            )}
            {isPending && (
              <>
                {isAdminOrUsersApprover &&
                  editLeaves &&
                  (onEditLeave ? (
                    <Button size="small" type="default" disabled={Boolean(approvedLevel)} onClick={() => onEditLeave(leaveRequestId)}>
                      <IntlMessages id="app.edit" />
                    </Button>
                  ) : (
                    <Button size="small" className="vt-edit-leave-btn" type="default" disabled={Boolean(approvedLevel)}>
                      <Link key={`${leaveRequestId}-edit`} to={`/app/leaves/${isToil ? 'toil/' : ''}${leaveRequestId}/edit`}>
                        <IntlMessages id="app.edit" />
                      </Link>
                    </Button>
                  ))}
                <Button size="small" onClick={(leave) => handleDenyWithReason(leave)} type="primary" danger>
                  <IntlMessages id="app.deny" />
                </Button>
                <Button size="small" loading={approveBtnLoading} onClick={handleApprove} type="primary">
                  <IntlMessages id="app.approve" />
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(LeavesBox)
